/**
 * Sprint 12+F (03.07.2026) — One-Click Pionier-Auto-Login-Landing-Page
 * Sprint 12+M4 (17.07.2026) — Email-Eingabe-Bridge fuer statische
 * KT-SmartLinks (KT setzt keine dynamischen URL-Parameter). User
 * bestaetigt durch aktive Email-Eingabe. Backend gleicht gegen
 * warteliste_entries ab, setzt Cookie, redirect zu /warteliste/danke.
 *
 * Routes:
 *   `/warteliste/one-click?token=<HMAC>` → HMAC-Token-Flow (Sprint 12+F)
 *   `/warteliste/one-click`               → Email-Eingabe-Bridge (Sprint 12+M4)
 *   `/warteliste/one-click?email=<mail>`  → Email vorausgefuellt (defensiv)
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, AlertCircle, CheckCircle2, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

// ---- HMAC-Token-Flow (Sprint 12+F, erweitert in M5.42 Block B) ----------
//
// Sprint M5.42 Block B (31.07.2026) — Statt Silent-Auto-Login zeigt der Flow
// jetzt eine explizite Email-Verifikation. Grund: Wir haben in Prod gesehen,
// dass Leute den Link auf Zweit-Devices/anderen Browsern öffnen und dabei
// versehentlich fremde Sessions triggern.
//
// Flow:
//   1) email_input   → User tippt Email, klickt „Weiter"
//   2) submitting    → POST /warteliste/one-click-opt-in mit input_email
//   3a) 200          → redirect zu /cockpit
//   3b) 400 mismatch → mismatch-State mit Retry-UI (siehe MismatchView)
//   3c) 401/404      → ErrorView

function HmacTokenFlow({ token }) {
  const { refresh } = useAuth() || {};
  const [state, setState] = useState("init"); // init | email_input | submitting | mismatch | retry_sending | retry_sent | error
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [mismatchMessage, setMismatchMessage] = useState("");
  const [retryCooldown, setRetryCooldown] = useState(0);

  // Parse user_id from token on mount
  useEffect(() => {
    try {
      const parts = token.split(".");
      if (parts.length !== 2) throw new Error("invalid_format");
      let b64 = parts[0].replace(/-/g, "+").replace(/_/g, "/");
      while (b64.length % 4) b64 += "=";
      const payload = JSON.parse(atob(b64));
      if (!payload.user_id) throw new Error("no_user_id");
      setUserId(payload.user_id);
      setState("email_input");
    } catch {
      setErrorCode("token_malformed");
      setState("error");
    }
  }, [token]);

  // Countdown-Timer fuer Rate-Limit
  useEffect(() => {
    if (retryCooldown <= 0) return undefined;
    const t = setTimeout(() => setRetryCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [retryCooldown]);

  async function submitEmail(e) {
    e?.preventDefault?.();
    if (!userId || !email.trim()) return;
    setState("submitting");
    setMismatchMessage("");
    try {
      const res = await fetch(`${API}/api/warteliste/one-click-opt-in`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          token,
          source: "soap06",
          input_email: email.trim(),
        }),
      });
      if (res.ok) {
        const body = await res.json();
        if (typeof refresh === "function") {
          try { await refresh(); } catch { /* non-fatal */ }
        }
        window.location.href = body.redirect_url || "/cockpit";
        return;
      }
      let code = `http_${res.status}`;
      let msg = "";
      try {
        const err = await res.json();
        if (typeof err.detail === "string") {
          code = err.detail;
        } else if (err.detail && typeof err.detail === "object") {
          code = err.detail.error || code;
          msg = err.detail.message || "";
        }
      } catch { /* keep default */ }
      if (code === "email_mismatch") {
        setMismatchMessage(msg);
        setState("mismatch");
      } else {
        setErrorCode(code);
        setState("error");
      }
    } catch {
      setErrorCode("network_error");
      setState("error");
    }
  }

  async function requestNewMagicLink() {
    if (!userId || retryCooldown > 0) return;
    setState("retry_sending");
    try {
      const res = await fetch(`${API}/api/warteliste/one-click-retry`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, old_token: token }),
      });
      if (res.ok) {
        const body = await res.json();
        setRetryCooldown(body.retry_after_seconds || 60);
        setState("retry_sent");
        return;
      }
      let code = `http_${res.status}`;
      let cooldown = 0;
      try {
        const err = await res.json();
        if (typeof err.detail === "string") {
          code = err.detail;
        } else if (err.detail && typeof err.detail === "object") {
          code = err.detail.error || code;
          cooldown = err.detail.retry_after_seconds || 0;
        }
      } catch { /* keep default */ }
      if (code === "rate_limited" && cooldown > 0) {
        setRetryCooldown(cooldown);
        setState("mismatch");
      } else {
        setErrorCode(code);
        setState("error");
      }
    } catch {
      setErrorCode("network_error");
      setState("error");
    }
  }

  function tryDifferentEmail() {
    setEmail("");
    setMismatchMessage("");
    setState("email_input");
  }

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12"
      data-testid="one-click-page"
    >
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-10">
        {state === "init" && (
          <div className="flex flex-col items-center text-center py-6" data-testid="one-click-loading">
            <Loader2 className="w-10 h-10 text-[#4563BB] animate-spin mb-4" />
            <p className="text-sm text-slate-500">Einen Moment...</p>
          </div>
        )}
        {(state === "email_input" || state === "submitting") && (
          <EmailVerifyForm
            email={email}
            setEmail={setEmail}
            onSubmit={submitEmail}
            submitting={state === "submitting"}
          />
        )}
        {state === "mismatch" && (
          <MismatchView
            message={mismatchMessage}
            onTryDifferent={tryDifferentEmail}
            onRequestNewLink={requestNewMagicLink}
            cooldown={retryCooldown}
          />
        )}
        {state === "retry_sending" && (
          <div className="flex flex-col items-center text-center py-6" data-testid="one-click-retry-sending">
            <Loader2 className="w-10 h-10 text-[#4563BB] animate-spin mb-4" />
            <p className="text-sm text-slate-500">Wir bereiten deinen neuen Link vor...</p>
          </div>
        )}
        {state === "retry_sent" && <RetrySentView cooldown={retryCooldown} />}
        {state === "error" && <ErrorView errorCode={errorCode} />}
      </div>
    </div>
  );
}

// ---- Email-Verify-Form (im HmacTokenFlow) -------------------------------

function EmailVerifyForm({ email, setEmail, onSubmit, submitting }) {
  return (
    <div data-testid="one-click-email-form-wrapper">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-full bg-[#4563BB]/10 items-center justify-center mb-4">
          <Mail className="w-7 h-7 text-[#4563BB]" />
        </div>
        <h1
          className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight"
          data-testid="one-click-email-headline"
        >
          Bestätige deine E-Mail
        </h1>
        <p className="mt-3 text-base text-slate-600 leading-relaxed">
          Damit wir sicher gehen, dass du es bist: Trag die E-Mail-Adresse
          ein, an die wir dir den Vorab-Einblick-Link geschickt haben.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="one-click-email-form">
        <div>
          <label
            htmlFor="one-click-email-input"
            className="block text-sm font-semibold text-slate-700 mb-1.5"
          >
            Deine E-Mail
          </label>
          <Input
            id="one-click-email-input"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="du@beispiel.de"
            disabled={submitting}
            className="h-12 text-base rounded-xl border-slate-300 focus-visible:ring-[#4563BB]"
            data-testid="one-click-email-input"
          />
        </div>

        <Button
          type="submit"
          disabled={submitting || !email.trim()}
          className="w-full h-12 rounded-xl bg-[#4563BB] hover:bg-[#3a54a2] text-white font-semibold text-base"
          data-testid="one-click-email-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Wird geprüft...
            </>
          ) : (
            <>Weiter</>
          )}
        </Button>
      </form>

      <p className="mt-6 text-xs text-slate-500 text-center">
        Kein Passwort nötig. Nach der Bestätigung wirst du automatisch eingeloggt.
      </p>
    </div>
  );
}

// ---- Mismatch-View (Retry-UI) -------------------------------------------

function MismatchView({ message, onTryDifferent, onRequestNewLink, cooldown }) {
  return (
    <div className="flex flex-col items-center text-center py-2" data-testid="one-click-mismatch">
      <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
        <AlertCircle className="w-8 h-8 text-amber-600" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
        Die E-Mail passt nicht.
      </h1>
      <p
        className="mt-3 text-base text-slate-600 leading-relaxed max-w-md"
        data-testid="one-click-mismatch-message"
      >
        {message || (
          <>
            Die eingegebene E-Mail-Adresse passt nicht zu der Adresse, an die
            wir dir geschrieben haben. Bitte nutze genau die Adresse, an der
            du unsere E-Mail erhalten hast.
          </>
        )}
      </p>
      <div className="mt-6 w-full space-y-3">
        <Button
          type="button"
          onClick={onTryDifferent}
          className="w-full h-12 rounded-xl bg-[#4563BB] hover:bg-[#3a54a2] text-white font-semibold text-base"
          data-testid="one-click-try-different-email"
        >
          Andere E-Mail probieren
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onRequestNewLink}
          disabled={cooldown > 0}
          className="w-full h-12 rounded-xl border-slate-300 text-slate-800 hover:bg-slate-50 font-semibold text-base"
          data-testid="one-click-request-new-link"
        >
          {cooldown > 0
            ? `Neuen Magic-Link anfordern (in ${cooldown}s)`
            : "Neuen Magic-Link anfordern"}
        </Button>
      </div>
    </div>
  );
}

// ---- Retry-Sent-View ----------------------------------------------------

function RetrySentView({ cooldown }) {
  return (
    <div className="flex flex-col items-center text-center py-2" data-testid="one-click-retry-sent">
      <div className="inline-flex w-14 h-14 rounded-full bg-emerald-100 items-center justify-center mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
        Neuer Link ist unterwegs.
      </h1>
      <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-md">
        Wir haben dir gerade eine neue E-Mail mit einem frischen Magic-Link
        geschickt. Prüfe bitte in den nächsten Minuten dein Postfach (auch
        den Spam-Ordner).
      </p>
      {cooldown > 0 && (
        <p
          className="mt-4 text-xs text-slate-600 font-medium"
          data-testid="one-click-retry-cooldown"
        >
          Du kannst in {cooldown}s einen weiteren Link anfordern.
        </p>
      )}
    </div>
  );
}


// ---- Email-Eingabe-Bridge (Sprint 12+M4) --------------------------------

function EmailBridgeFlow({ prefillEmail }) {
  const [email, setEmail] = useState(prefillEmail || "");
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (state === "submitting") return;
    const clean = email.trim().toLowerCase();
    if (!clean) return;
    setState("submitting");
    setErrorMessage("");
    try {
      const res = await fetch(`${API}/api/warteliste/confirm-email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: clean }),
      });
      if (res.ok) {
        setState("success");
        // Sprint 12+M5.17-g (22.07.2026) — Doppelte-Eingabe-Fix:
        // `/warteliste/danke` prueft neben AuthContext auch URL-Params.
        // Falls der Cookie zwischen One-Click-Bridge und Danke-Seite
        // (Safari ITP, Cross-Tab) verlorengeht, greift der `?email=`-
        // Auto-Rehydrate im Backend (`/warteliste/verify?email=...`)
        // und setzt Cookie neu. Damit ist die Userin nach genau EINER
        // E-Mail-Eingabe stabil eingeloggt bis ins Cockpit.
        const nextUrl =
          "/warteliste/danke?email=" + encodeURIComponent(clean);
        setTimeout(() => {
          window.location.href = nextUrl;
        }, 2000);
      } else {
        let msg = "Etwas ist schiefgelaufen. Bitte versuch's gleich noch mal.";
        try {
          const body = await res.json();
          if (body?.detail?.message) {
            msg = body.detail.message;
          } else if (typeof body?.detail === "string") {
            msg = body.detail;
          }
        } catch { /* keep default */ }
        setErrorMessage(msg);
        setState("error");
      }
    } catch {
      setErrorMessage("Wir konnten den Server nicht erreichen. Bitte prüfe deine Verbindung.");
      setState("error");
    }
  }

  if (state === "success") {
    return (
      <div
        className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
        data-testid="email-bridge-success"
      >
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-10 text-center">
          <div className="inline-flex w-14 h-14 rounded-full bg-emerald-100 items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Du bist dabei.
          </h1>
          <p className="mt-3 text-base text-slate-600 leading-relaxed">
            Als Flex4Success-Pionier erfährst du als Erstes, wenn es
            spannende Neuigkeiten gibt.
          </p>
          <Loader2 className="w-5 h-5 text-[#4563BB] animate-spin mt-5 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12"
      data-testid="email-bridge-page"
    >
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-10">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-full bg-[#4563BB]/10 items-center justify-center mb-4">
            <Mail className="w-7 h-7 text-[#4563BB]" />
          </div>
          <h1
            className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight"
            data-testid="email-bridge-headline"
          >
            Du bist als Pionier eingeladen.
          </h1>
          <p
            className="mt-3 text-base text-slate-600 leading-relaxed"
            data-testid="email-bridge-microcopy"
          >
            Trag die E-Mail ein, an die ich dir gerade geschrieben habe.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="email-bridge-form">
          <div>
            <label
              htmlFor="email-bridge-input"
              className="block text-sm font-semibold text-slate-700 mb-1.5"
            >
              Deine E-Mail
            </label>
            <Input
              id="email-bridge-input"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="du@beispiel.de"
              disabled={state === "submitting"}
              className="h-12 text-base rounded-xl border-slate-300 focus-visible:ring-[#4563BB]"
              data-testid="email-bridge-input"
            />
          </div>

          {state === "error" && (
            <div
              className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3"
              data-testid="email-bridge-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={state === "submitting" || !email.trim()}
            className="w-full h-12 rounded-xl bg-[#4563BB] hover:bg-[#3a54a2] text-white font-semibold text-base"
            data-testid="email-bridge-submit"
          >
            {state === "submitting" ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Wird bestätigt...
              </>
            ) : (
              <>Ja, ich bin dabei</>
            )}
          </Button>
        </form>

        <p className="mt-6 text-xs text-slate-500 text-center">
          Kein Passwort nötig. Du wirst automatisch eingeloggt.
        </p>
      </div>
    </div>
  );
}

// ---- ErrorView (shared, used by HMAC-Token-Flow) ------------------------

function ErrorView({ errorCode }) {
  const messageMap = {
    token_malformed: {
      title: "Link nicht lesbar.",
      body: "Der Link wurde ungültig oder abgeschnitten. Bitte klicke den Link direkt in deiner Mail an.",
    },
    invalid_token: {
      title: "Link nicht mehr gültig.",
      body: "Der One-Click-Link ist abgelaufen oder ungültig. Kein Problem — du kannst dich manuell auf die Vorab-Einblick-Liste eintragen.",
    },
    token_invalidated: {
      title: "Link wurde bereits ersetzt.",
      body: "Du hast einen neuen Magic-Link angefordert. Bitte prüfe dein Postfach und klicke den neuesten Link an — der ältere ist nicht mehr gültig.",
    },
    user_id_mismatch: {
      title: "Link passt nicht.",
      body: "Etwas stimmt mit dem Link nicht. Bitte melde dich manuell an.",
    },
    user_not_found: {
      title: "Wir konnten dich nicht finden.",
      body: "Bitte melde dich manuell auf der Vorab-Einblick-Liste an — wir aktivieren dich dann.",
    },
    network_error: {
      title: "Verbindungsproblem.",
      body: "Wir konnten den Server nicht erreichen. Bitte prüfe deine Verbindung und versuche es erneut.",
    },
    unknown: {
      title: "Auto-Login fehlgeschlagen.",
      body: "Etwas ist schiefgelaufen. Bitte melde dich manuell auf der Vorab-Einblick-Liste an.",
    },
  };
  const msg = messageMap[errorCode] || messageMap.unknown;
  return (
    <div
      className="flex flex-col items-center text-center py-6"
      data-testid="one-click-error"
    >
      <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <AlertCircle className="w-8 h-8 text-red-600" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
        {msg.title}
      </h1>
      <p className="mt-3 text-base text-slate-600 max-w-md">
        {msg.body}
      </p>
      <p
        className="mt-5 text-sm text-slate-500"
        data-testid="one-click-error-code"
      >
        Fehler-Code: <span className="font-mono">{errorCode}</span>
      </p>
      <Link to="/warteliste">
        <Button
          className="mt-6 bg-[#4563BB] hover:bg-[#3a54a3] text-white"
          data-testid="one-click-fallback-link"
        >
          Manuell zur Vorab-Einblick-Liste →
        </Button>
      </Link>
    </div>
  );
}

// ---- Root component: waehlt Flow anhand URL-Param ----------------------

export default function OneClickOptIn() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();
  const prefillEmail = (searchParams.get("email") || "").trim();

  if (token) {
    return <HmacTokenFlow token={token} />;
  }
  return <EmailBridgeFlow prefillEmail={prefillEmail} />;
}
